import { z } from 'zod';
import type { EmailStatus } from './emails.js';
import { SubscriberAttributesSchema } from './email-lists.js';

// ─────────────────────────────────────────────────────────────────
// Email sending (a "send", not a send attempt)
//
// An email is *content*; a send is one queuing of that content to an
// audience. Everything a send needs to be reproducible is snapshotted at
// queue time (POST /api/email-sends):
//
//   · email_sending_info  — who it goes out as, and the timeline
//   · email_targets       — the selected lists (or legacy segments)
//   · email_recipients    — the addresses those targets resolved to, queued,
//                           each with its own merge values frozen
//
// So the rows are the record: editing a list, adding a subscriber or
// deleting the email's content cannot change a send that is already queued.
// The background sender job (apps/server/src/jobs/email-sender.ts) drains
// queued recipients and moves each one to delivered/failed/bounced.
// ─────────────────────────────────────────────────────────────────

/** What a send can be targeted at. Lists are the model the editor uses. */
export const EMAIL_SEND_AUDIENCE_TYPES = ['list', 'segment'] as const;
export const EmailSendAudienceTypeSchema = z.enum(EMAIL_SEND_AUDIENCE_TYPES);
export type EmailSendAudienceType = z.infer<typeof EmailSendAudienceTypeSchema>;

/**
 * One audience a send was targeted at, as returned by the API. `name` is the
 * list's name today; a list deleted after the send leaves the target row with
 * it (the FK cascades), so a send can end up with fewer targets than it had —
 * its recipient records are what remain complete.
 */
export const EmailSendTargetSchema = z.object({
  type: EmailSendAudienceTypeSchema,
  id: z.string(),
  name: z.string().nullable(),
});
export type EmailSendTarget = z.infer<typeof EmailSendTargetSchema>;

/**
 * A send's own status, derived server-side from the row's timeline and its
 * recipients — never stored, so it cannot disagree with them:
 *
 *   cancelled → called off before it finished
 *   queued    → waiting for its scheduled time (or for the sender job)
 *   sending   → started, recipients still queued
 *   sent      → finished with nothing left queued
 *   failed    → finished with every recipient failed
 */
export const EMAIL_SEND_STATUSES = ['queued', 'sending', 'sent', 'failed', 'cancelled'] as const;
export const EmailSendStatusSchema = z.enum(EMAIL_SEND_STATUSES);
export type EmailSendStatus = z.infer<typeof EmailSendStatusSchema>;

/** Recipient tallies for one send. `total` is the snapshot's size. */
export const EmailSendCountsSchema = z.object({
  total: z.number().int(),
  queued: z.number().int(),
  delivered: z.number().int(),
  bounced: z.number().int(),
  complained: z.number().int(),
  failed: z.number().int(),
});
export type EmailSendCounts = z.infer<typeof EmailSendCountsSchema>;

/** A send that has counted nothing yet — the zero value of {@link EmailSendCounts}. */
export function emptyCounts(): EmailSendCounts {
  return { total: 0, queued: 0, delivered: 0, bounced: 0, complained: 0, failed: 0 };
}

/**
 * The part of a send's row that decides its status. Structural rather than the
 * database row type so this stays usable from both the API layer and the
 * platform-admin metrics without either importing the other.
 */
export interface SendTimeline {
  cancelledAt: string | null;
  completedAt: string | null;
  startedAt: string | null;
}

/**
 * Derive a send's status from its timeline and its recipient tally — never
 * stored, so it cannot disagree with either. `cancelled` is checked first: a
 * send called off mid-flight keeps its recipients, and "cancelled" is the
 * honest description of it however many were already delivered.
 */
export function deriveSendStatus(row: SendTimeline, counts: EmailSendCounts): EmailSendStatus {
  if (row.cancelledAt) return 'cancelled';
  if (row.completedAt) {
    return counts.total > 0 && counts.failed === counts.total ? 'failed' : 'sent';
  }
  return row.startedAt ? 'sending' : 'queued';
}

/**
 * What resuming a stopped send does to its *email's* status: `scheduled`, or
 * null for "leave it alone".
 *
 * Cancelling a send that had not started drops its email back to a draft when
 * nothing else was pending, so resuming that send puts the email back where the
 * queue had it. Only that case moves: an immediate send never changed the
 * email's status, a send that had already started left it to the job, and an
 * email that has since been edited — published, archived, sent — is not
 * relabelled by a send being un-stopped underneath it.
 */
export function emailStatusAfterResume(input: {
  /** The send's own timeline. */
  startedAt: string | null;
  scheduledAt: string | null;
  /** The email's status right now. */
  emailStatus: EmailStatus;
}): 'scheduled' | null {
  if (input.startedAt || !input.scheduledAt) return null;
  return input.emailStatus === 'draft' ? 'scheduled' : null;
}

/** A send as returned by the API. */
export const EmailSendSchema = z.object({
  id: z.string(),
  emailId: z.string(),
  /** The email's subject at read time — content, not part of the snapshot. */
  emailSubject: z.string(),
  status: EmailSendStatusSchema,
  senderId: z.string().uuid(),
  senderName: z.string().nullable(),
  fromName: z.string(),
  fromAddress: z.string(),
  replyTo: z.string().nullable(),
  targets: z.array(EmailSendTargetSchema),
  recipients: EmailSendCountsSchema,
  scheduledAt: z.string().nullable(),
  startedAt: z.string().nullable(),
  completedAt: z.string().nullable(),
  cancelledAt: z.string().nullable(),
  createdAt: z.string(),
  /**
   * The file a **CSV send** was queued from, or null for a send built from
   * lists. A CSV send's temporary audience is deleted once it finishes, so
   * this is the only thing left in History that says where the recipients came
   * from.
   */
  csvFilename: z.string().nullable(),
});
export type EmailSend = z.infer<typeof EmailSendSchema>;

export const PaginatedEmailSendsSchema = z.object({
  data: z.array(EmailSendSchema),
  total: z.number().int(),
  page: z.number().int(),
  limit: z.number().int(),
  totalPages: z.number().int(),
});
export type PaginatedEmailSends = z.infer<typeof PaginatedEmailSendsSchema>;

/** One recipient row of a send — the delivery record, not the merge values. */
export const EmailSendRecipientSchema = z.object({
  id: z.string(),
  email: z.string(),
  status: z.enum(['queued', 'delivered', 'bounced', 'complained', 'failed']),
  /**
   * Whether a copy of what this recipient received was kept, not the copy
   * itself: it is the rendered email, so it is fetched for the one recipient
   * somebody opens (see `EmailSendRecipientContentSchema`) rather than carried
   * by every row of a page. False for a recipient that has not been delivered
   * yet, and for every recipient of a send that failed to render.
   */
  hasContentSnapshot: z.boolean(),
  errorMessage: z.string().nullable(),
  openedAt: z.string().nullable(),
  clickedAt: z.string().nullable(),
  bouncedAt: z.string().nullable(),
});
export type EmailSendRecipient = z.infer<typeof EmailSendRecipientSchema>;

/**
 * The copy one recipient was actually sent — the rendered HTML the sender job
 * handed to SMTP, with that recipient's merge values and opt-out link already
 * applied. An image sent as an attachment is a `cid:` reference here, exactly
 * as it was in the mail: the attachment bytes are not kept. `null` when nothing
 * was kept (a recipient that has not been delivered), which is a different
 * answer from "no such recipient" (404).
 */
export const EmailSendRecipientContentSchema = z.object({
  recipientId: z.string(),
  email: z.string(),
  status: z.enum(['queued', 'delivered', 'bounced', 'complained', 'failed']),
  contentSnapshot: z.string().nullable(),
});
export type EmailSendRecipientContent = z.infer<typeof EmailSendRecipientContentSchema>;

export const PaginatedEmailSendRecipientsSchema = z.object({
  data: z.array(EmailSendRecipientSchema),
  total: z.number().int(),
  page: z.number().int(),
  limit: z.number().int(),
  totalPages: z.number().int(),
});
export type PaginatedEmailSendRecipients = z.infer<typeof PaginatedEmailSendRecipientsSchema>;

export const EmailSendRecipientsQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  /** Filter by delivery status; omitted means every recipient. */
  status: z.enum(['queued', 'delivered', 'bounced', 'complained', 'failed']).optional(),
});
export type EmailSendRecipientsQuery = z.infer<typeof EmailSendRecipientsQuerySchema>;

export const EmailSendListQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(20),
  /** Narrow to the sends of one email. */
  emailId: z.string().optional(),
});
export type EmailSendListQuery = z.infer<typeof EmailSendListQuerySchema>;

// ── Queue a send ──────────────────────────────────────────────────

/**
 * Values for the email's own `variableDefinition`, used as the *base* for
 * every recipient. A subscriber's own attribute values are layered over them
 * per recipient, so a list's `first_name` wins over a value set here.
 *
 * A variable the email marks required must be satisfied one way or the other:
 * by a value here, or by the audience — at least one subscriber in the
 * selected lists has to carry *every* required variable that no value covers.
 * A list merely *defining* the attribute is not enough, because a definition
 * with no values behind it renders an empty merge field for everyone.
 */
export const EmailSendVariablesSchema = z.record(z.string(), z.unknown());

export const CreateEmailSendSchema = z.object({
  emailId: z.string().min(1),
  /** A stored sender (POST /api/emails/senders). Only SMTP senders send today. */
  senderId: z.string().uuid('Invalid sender id'),
  /** The audience: at least one list, and every id must be in this workspace. */
  listIds: z.array(z.string().uuid('Invalid list id')).min(1, 'Pick at least one list'),
  /**
   * When to send. Omitted/`null` ⇒ as soon as the sender job next runs.
   * A time in the past is clamped to now rather than refused — a clock skew
   * between the browser and the server must not lose the send.
   */
  scheduledAt: z.string().datetime({ offset: true }).nullable().optional(),
  /** Base merge values; per-recipient attribute values override them. */
  variables: EmailSendVariablesSchema.optional(),
  /** Display name override. Defaults to the sender's own. */
  fromName: z.string().trim().max(100).optional(),
  /** From address override. Defaults to the sender's own. */
  fromAddress: z.string().email('Invalid From address').optional(),
  replyTo: z.string().email('Invalid reply-to address').nullable().optional(),
});

export type CreateEmailSendInput = z.infer<typeof CreateEmailSendSchema>;

/**
 * What queueing a send returns: the send itself plus the size of the snapshot
 * it just took. `recipientCount` is the number of *distinct* addresses queued —
 * a contact in two selected lists is queued once.
 */
export const CreateEmailSendResultSchema = z.object({
  send: EmailSendSchema,
  recipientCount: z.number().int(),
  /** Addresses skipped because the send already held them (re-sends). */
  duplicates: z.number().int(),
});
export type CreateEmailSendResult = z.infer<typeof CreateEmailSendResultSchema>;

/**
 * Preview what an audience selection would resolve to, without queueing
 * anything: the audience's total size and what each list contributes.
 */
export const EmailSendPreviewSchema = z.object({
  /** Distinct addresses across every selected list. */
  total: z.number().int(),
  lists: z.array(
    z.object({
      id: z.string().uuid(),
      name: z.string(),
      subscriberCount: z.number().int(),
    }),
  ),
  /**
   * Attribute keys the selected lists can merge, so the send form can say
   * whether the email's variables will be personalised per recipient.
   */
  attributeKeys: z.array(z.string()),
  /**
   * The email's required variables that neither a supplied value nor the
   * audience satisfies — no subscriber in the selected lists carries all of
   * them. Non-empty ⇒ POST /api/email-sends (and POST /api/v1/sends) is a 422.
   */
  missingVariables: z.array(z.string()),
});
export type EmailSendPreview = z.infer<typeof EmailSendPreviewSchema>;

/**
 * Body of POST /api/email-sends/preview — what the audience selection would
 * resolve to, before anything is queued. A body rather than a query string
 * because the selection is a list, and it can be a long one.
 */
export const EmailSendPreviewRequestSchema = z.object({
  emailId: z.string().min(1),
  listIds: z.array(z.string().uuid('Invalid list id')).min(1, 'Pick at least one list'),
  variables: EmailSendVariablesSchema.optional(),
});
export type EmailSendPreviewRequest = z.infer<typeof EmailSendPreviewRequestSchema>;

/** Body of the cancel route. */
export const CancelEmailSendSchema = z.object({
  /**
   * Cancel even when the send has already started. Off by default: a running
   * send is stopped only when the caller says so explicitly.
   */
  force: z.boolean().default(false),
});
export type CancelEmailSendInput = z.infer<typeof CancelEmailSendSchema>;

/** Attribute values a recipient may be personalised with (same as a subscriber's). */
export const EmailSendMergeValuesSchema = SubscriberAttributesSchema;
export type EmailSendMergeValues = z.infer<typeof EmailSendMergeValuesSchema>;
